import { title } from 'process';
import { PostQuoteResponse } from '../../../types/PostQuoteResponse';
import Trip from '../../../types/Trip';
import getTripKeyDetails from '../../../utilities/getTripKeyDetails';
import Card from '../../molecules/Card/Card';
import Heading from '../../typography/Heading';
import Link from '../../atoms/Link/Link';
import { TripExpense } from '../../../types/TripExpense';
import Rule from '../../atoms/Rule';
import getFinalExpenseTotal from '../../../utilities/getFinalExpenseTotal';
import getGroupedExpenses from '../../../utilities/getGroupedExpenses';

interface ExpenseAndAlterationSummaryCardProps {
  trip: Trip;
}

function ExpenseAndAlterationSummaryCard({
  trip,
}: ExpenseAndAlterationSummaryCardProps) {
  const { hiredBidConversation } = getTripKeyDetails(trip);
  if (!hiredBidConversation || !hiredBidConversation.bid)
    throw new Error(
      `No hired bid conversation for ExpenseAndAlterationSummaryCard`
    );

  const { cost, bid } = hiredBidConversation;
  const { expenses } = bid;

  const finalExpenseTotal = getFinalExpenseTotal(expenses);
  const groupedExpenses = getGroupedExpenses(expenses);

  const combinedExpenses: TripExpense[] = Object.values(groupedExpenses);

  const {
    totalCharge,
    dayTwoCharge,
    pilotFee,
    platformFee,
    flyingCost,
    travelCost,
    diem,
    dayDiem,
    flightDays,
    travelDays,
    dayOneTravelFee,
    finalPaymentTravelFee,
    totalDayCost,
    pricePerDay,
    dayOneTenPServiceFee,
    dayOneStripeFee,
    expenseCost,
    tripDuration,

    // Altered fields grouped together
    alteredTotalCharge,
    alteredPilotFee,
    alteredPlatformFee,
    alteredFlyingCost,
    alteredTravelCost,
    alteredTripDuration,
    alteredFlyingDays,
    alteredTravelDays,
    alterationDays,
    alterationDiem,
  } = cost.estimation;

  return (
    <Card responsive noPadding>
      <div className="py-2 background bg-primary rounded-t-lg">
        <Heading
          level={4}
          className="uppercase text-center text-white font-normal py-2"
        >
          Updated Invoice
        </Heading>
      </div>
      <div className="flex flex-col gap-2 h-full">
        <div className="flex flex-col gap-2 p-4">
          <div className="flex">
            <span>
              Flying Day-Rate ({alteredFlyingDays} days, $
              {pricePerDay.toFixed(2)}/day):{' '}
            </span>
            <b className="ml-auto">${alteredFlyingCost.toFixed(2)}</b>
          </div>
          <div className="flex">
            <span>
              Travel Day-Rate ({alteredTravelDays} days, $
              {(alteredTravelCost / alteredTravelDays).toFixed(2)}
              /day):
            </span>
            <b className="ml-auto">
              ${cost.estimation.alteredTravelCost.toFixed(2)}
            </b>
          </div>
          <div className="flex">
            <span>Per-Diem ({alteredTripDuration} days, $100/day): </span>
            <b className="ml-auto">${dayDiem * alteredTripDuration}</b>
          </div>
          <div className="flex">
            <span>Service Fee:</span>
            <span className="ml-auto font-semibold mr-2">
              ${alteredPlatformFee.toFixed(2)}
            </span>
            <Link href="https://helpcenter.flyingcompany.com/en/help/articles/1092605-flying-company-service-fees">
              <i className="fa-solid fa-circle-question" />
            </Link>
          </div>
        </div>
        <div className="py-2 w-full background bg-primary">
          <Heading
            level={4}
            className="uppercase text-center text-white font-normal py-2"
          >
            Pre-expense Total: $
            {(
              alteredFlyingCost +
              alteredTravelCost +
              dayDiem * alteredTripDuration +
              alteredPlatformFee
            ).toFixed(2)}
          </Heading>
        </div>
        <div className="flex flex-col gap-2 p-4 flex-grow">
          {combinedExpenses.length
            ? combinedExpenses.map((expense) => (
                <div className="flex" key={expense.title}>
                  <span>{expense.title}:</span>
                  <span className="ml-auto font-semibold border-b border-black border-dotted">
                    $
                    {expense && expense.cost && typeof expense.cost === 'number'
                      ? expense.cost.toFixed(2)
                      : 0}
                  </span>
                </div>
              ))
            : 'Pending'}
          {combinedExpenses.length ? (
            <>
              <Rule className="my-2" />
              <div className="flex">
                <span className="font-bold">Final expense total:</span>
                <span className="ml-auto font-semibold border-b border-black border-dotted">
                  ${finalExpenseTotal.toFixed(2)}
                </span>
              </div>
            </>
          ) : (
            ''
          )}
        </div>
        <div className="py-2 background bg-primary rounded-b-lg">
          <Heading
            level={4}
            className="uppercase text-center text-white font-normal py-2"
          >
            Final Total: ${alteredTotalCharge}
          </Heading>
        </div>
      </div>
    </Card>
  );
}

export default ExpenseAndAlterationSummaryCard;
